import { Dialog, DialogContent, IconButton, Typography, Box, Chip } from '@mui/material';
import { Phone, CallEnd, Mic, MicOff, Pause, PlayArrow, Minimize, FiberManualRecord, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useVoIP } from '../hooks/useVoIP';
import { useCallTimer } from '../hooks/useCallTimer';
import { CallState } from '../types';

export const DialerModal = () => {
  const { t } = useTranslation();
  const {
    callState,
    currentCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    isMuted,
    isOnHold,
    closeDialer,
    minimizeDialer,
    isRecording,
    isUploading,
  } = useVoIP();

  const duration = useCallTimer(currentCall?.startTime, callState === CallState.CONNECTED);

  const getCallStateLabel = () => {
    switch (callState) {
      case CallState.INCOMING:
        return t('voip.incoming_call');
      case CallState.OUTGOING:
        return t('voip.calling');
      case CallState.CONNECTING:
        return t('voip.connecting');
      case CallState.CONNECTED:
        return duration;
      case CallState.ON_HOLD:
        return t('voip.on_hold');
      case CallState.ENDED:
        return t('voip.call_ended');
      default:
        return '';
    }
  };

  const getDisplayInfo = () => {
    if (currentCall?.contactInfo?.name) {
      return currentCall.contactInfo.name;
    }
    return currentCall?.phoneNumber || t('voip.unknown');
  };

  return (
    <Dialog
      open={true}
      onClose={() => callState === CallState.IDLE && closeDialer()}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton size="small" onClick={minimizeDialer}>
          <Minimize />
        </IconButton>
      </Box>

      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" gutterBottom>
          {getDisplayInfo()}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: (isRecording && callState === CallState.CONNECTED) || (isUploading && callState === CallState.ENDED) ? 1 : 4 }}>
          {getCallStateLabel()}
        </Typography>

        {isRecording && callState === CallState.CONNECTED && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Chip
              icon={<FiberManualRecord sx={{ color: 'error.main', animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.3 }, '100%': { opacity: 1 } } }} />}
              label="REC"
              size="small"
              sx={{ color: 'error.main', borderColor: 'error.main', fontWeight: 'bold' }}
              variant="outlined"
            />
          </Box>
        )}

        {callState === CallState.ENDED && isUploading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3, p: 1.5, bgcolor: 'warning.light', borderRadius: 2 }}>
            <Warning sx={{ color: 'warning.dark', fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 500 }}>
              {t('voip.saving_recording')}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
          {(callState === CallState.CONNECTED || callState === CallState.ON_HOLD) && (
            <IconButton
              onClick={toggleMute}
              sx={{
                bgcolor: isMuted ? 'error.main' : 'action.hover',
                color: isMuted ? 'white' : 'inherit',
                '&:hover': {
                  bgcolor: isMuted ? 'error.dark' : 'action.selected',
                },
              }}
            >
              {isMuted ? <MicOff /> : <Mic />}
            </IconButton>
          )}

          {(callState === CallState.CONNECTED || callState === CallState.ON_HOLD) && (
            <IconButton
              onClick={toggleHold}
              sx={{
                bgcolor: isOnHold ? 'warning.main' : 'action.hover',
                color: isOnHold ? 'white' : 'inherit',
                '&:hover': {
                  bgcolor: isOnHold ? 'warning.dark' : 'action.selected',
                },
              }}
            >
              {isOnHold ? <PlayArrow /> : <Pause />}
            </IconButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          {callState === CallState.INCOMING && (
            <IconButton
              onClick={answerCall}
              sx={{
                bgcolor: 'success.main',
                color: 'white',
                width: 64,
                height: 64,
                '&:hover': {
                  bgcolor: 'success.dark',
                },
              }}
            >
              <Phone sx={{ fontSize: 32 }} />
            </IconButton>
          )}

          {callState !== CallState.IDLE && callState !== CallState.ENDED && (
            <IconButton
              onClick={hangUp}
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                width: 64,
                height: 64,
                '&:hover': {
                  bgcolor: 'error.dark',
                },
              }}
            >
              <CallEnd sx={{ fontSize: 32 }} />
            </IconButton>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
